<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import { dayjs } from '@/lib/dayjs'
import { httpPost } from '@/lib/http'

const EXPIRATION_WARNING_THRESHOLD = [1, 'year'] as const

interface Certificate {
  ace_course_id: number
  ace_course_name: string
  expiry_date: string
}

const open = ref(false)
const error = ref('')
const certificates = ref<Certificate[]>([])
const selectedCourseId = ref<number | null>(null)

const certsList = computed(() =>
  certificates.value.map(cert => {
    const expiryDate = dayjs(cert.expiry_date)
    const warningThresholdDate = expiryDate.subtract(...EXPIRATION_WARNING_THRESHOLD)
    const expired = expiryDate.isBefore(dayjs())
    const expiryWarning = dayjs().isAfter(warningThresholdDate) && !expired
    const expiryDateStr = expiryDate.format('до DD.MM.YYYY')

    return {
      ...cert,
      expired,
      expiryWarning,
      expiryDateStr
    }
  })
)

const prolongUrl = computed(() => {
  if (!selectedCourseId.value) return null
  return `/seminars/get_ace_prolong_page?ace_course_id=${selectedCourseId.value}`
})

const fetchCertificates = debounce(async () => {
  try {
    const {
      active_aces: data,
      failed,
      message
    } = await httpPost<{
      active_aces: Certificate[]
      failed: boolean
      message?: string
    }>('/seminars/request_teacher_active_aces')

    if (failed) throw message

    certificates.value = data
  } catch (err) {
    let errorText = 'Не удалось получить список сертификатов. Повторите попытку позже.'

    if (typeof err === 'string') errorText = err
    error.value = errorText
  }
}, 300)

watch(open, val => {
  if (val) fetchCertificates()
  else certificates.value = []
})

function openModal() {
  open.value = true
}

defineExpose({
  $open: openModal
})
</script>

<template>
  <v-rich-modal v-model="open" title="Продление сертификатов">
    <div class="ace-prolong-modal">
      <div v-if="error" class="ace-prolong-modal__preloader red-text">
        <v-icon size="48">mdi-alert-circle-outline</v-icon>
        <span class="mt-2" v-text="error" />
      </div>

      <div v-else-if="!certificates.length" class="ace-prolong-modal__preloader blue-text">
        <v-icon size="48" class="mdi-spin">mdi-timer-sand</v-icon>
        <span class="mt-2">Загрузка...</span>
      </div>

      <ul v-else class="ace-prolong-modal__cert-list">
        <li
          v-for="cert in certsList"
          :key="cert.ace_course_id"
          @click="
            () => {
              selectedCourseId = cert.ace_course_id
            }
          "
        >
          <v-radio
            name="ace-prolong-course-id"
            :active="selectedCourseId === cert.ace_course_id"
            :value="cert.ace_course_id"
            @update:model-value="
              () => {
                selectedCourseId = cert.ace_course_id
              }
            "
          />

          <v-icon v-if="cert.expired" key="expired">cert-expired</v-icon>
          <v-icon v-else key="active">cert-done</v-icon>

          <span class="flex-spacer text-body-bold" v-text="cert.ace_course_name" />

          <v-icon v-if="cert.expiryWarning" class="red-text">mdi-alert</v-icon>
          <span :class="{ 'red-text': cert.expiryWarning, 'gray-text': cert.expired }" v-text="cert.expiryDateStr" />
        </li>
      </ul>
    </div>

    <template v-if="error" #actions>
      <v-button
        mode="primary large block"
        @click="
          () => {
            error = ''
            fetchCertificates()
          }
        "
      >
        Повторить попытку
      </v-button>
      <div class="ma-1" />
      <v-button
        mode="outlined large block"
        @click="
          () => {
            open = false
          }
        "
      >
        Закрыть
      </v-button>
    </template>
    <template v-else #actions>
      <v-button mode="primary large block" :disabled="!selectedCourseId" :href="prolongUrl"> Продлить </v-button>
    </template>
  </v-rich-modal>
</template>

<style lang="scss">
////
.ace-prolong-modal {
  width: min(550px, 100vw);

  &__preloader {
    padding: 24px 0;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    @include mobile {
      height: 100%;
    }
  }

  &__cert-list {
    display: flex;
    flex-direction: column;
    gap: 16px;

    & > li {
      display: flex;
      align-items: center;
      gap: 8px;

      cursor: pointer;
    }
  }
}
</style>
